import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Mail } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Field, Loading, Modal, Section, StatusPill, cx } from "./wf";
import { api, money, num, todayISO } from "../lib/api";

export const DOC_WARN_DAYS = 30;
export const daysUntil = (date) => Math.round((new Date(`${date}T00:00`) - new Date(`${todayISO()}T00:00`)) / 86400000);
const CLOSED = ["complete", "completed", "invoiced", "paid"];
const isClosed = (s) => CLOSED.includes((s ?? "").trim().toLowerCase());

/** Active staff with documents expired or expiring within 30 days. */
export function ExpiringDocsBanner({ employees }) {
  const rows = useMemo(
    () => employees.filter((e) => (e.status || "active") === "active")
      .flatMap((e) => (e.documents ?? []).filter((d) => d.expires_on && daysUntil(d.expires_on) <= DOC_WARN_DAYS).map((d) => ({ emp: e, doc: d, days: daysUntil(d.expires_on) })))
      .sort((a, b) => a.days - b.days),
    [employees],
  );
  if (!rows.length) return null;
  const expired = rows.filter((r) => r.days < 0).length;
  return (
    <div data-testid="expiring-docs-banner" className={cx("mb-5 rounded-2xl border p-4", expired ? "border-red-200 bg-red-50" : "border-orange-200 bg-orange-50")}>
      <p className="mb-2 flex items-center gap-2 font-bold">
        <AlertTriangle size={18} className={expired ? "text-wf-err" : "text-wf-warn"} />
        {rows.length} document{rows.length === 1 ? "" : "s"} {expired ? `need${rows.length === 1 ? "s" : ""} attention` : "expiring soon"}
      </p>
      {rows.slice(0, 5).map(({ emp, doc, days }) => (
        <Link key={`${emp.id}-${doc.id}`} to={`/employees/${emp.id}`} data-testid={`expiring-doc-${doc.id}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/60">
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{emp.name}</span><span className="block truncate text-sm text-wf-mute">{doc.description || "Document"} · {doc.expires_on}</span></span>
          <span className={cx("text-sm font-bold", days < 0 ? "text-wf-err" : "text-wf-warn")}>{days < 0 ? "Expired" : days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"}`}</span>
          <ChevronRight size={16} className="text-wf-mute" />
        </Link>
      ))}
      {rows.length > 5 && <p className="px-2 text-sm text-wf-mute">+ {rows.length - 5} more</p>}
    </div>
  );
}

/** Email someone a 7-day view link to one employee document. */
export function ShareDocDialog({ target, onClose }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) {
      setEmail("");
      setMessage("");
    }
  }, [target]);
  const send = async () => {
    const to = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      await api(`/employees/${target.employeeId}/documents/${target.docId}/share`, { method: "POST", body: { email: to, message } });
      toast.success(`Document sent to ${to}`);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={!!target} onClose={onClose} title="Share by Email" testid="share-doc-sheet">
      <p className="text-sm text-wf-mute">{target?.title || "Document"} · they get a link to view it for 7 days</p>
      <Field type="email" testid="share-doc-email" value={email} onChange={setEmail} placeholder="name@example.com" />
      <Field multiline testid="share-doc-message" value={message} onChange={setMessage} placeholder="Message (optional)" />
      <div className="flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose} data-testid="share-doc-cancel">Cancel</Btn>
        <Btn icon={Mail} loading={busy} onClick={send} data-testid="share-doc-send">Send</Btn>
      </div>
    </Modal>
  );
}

/** All jobs assigned to one Kastle PM with combined totals. */
export function KastleJobs({ pmId, seeMoney }) {
  const q = useQuery({ queryKey: ["work-orders", { kastle: pmId }], queryFn: () => api(`/work-orders?kastle_pm_id=${pmId}`) });
  const all = q.data ?? [];
  const sum = (k) => all.reduce((a, w) => a + (w.totals?.[k] ?? 0), 0);
  const stats = [
    ["Open Jobs", `${all.filter((w) => !isClosed(w.status)).length} / ${all.length}`, "open"],
    ["Total Hours", num(sum("total_hours")), "hours", `${num(sum("man_hours"))} man hrs`],
    ...(seeMoney ? [["Working Budget", money(sum("working_budget")), "working-budget"], ["Project Total", money(sum("total_cost")), "cost"], ["Remaining", money(sum("budget_remaining")), "remaining"]] : []),
  ];
  return (
    <div data-testid="kastle-jobs">
      <Section title={`Jobs (${all.length})`} />
      {q.isLoading ? <Loading /> : !all.length ? <p className="text-sm text-wf-mute">No jobs assigned to this project manager yet.</p> : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="kastle-jobs-totals">
            {stats.map(([l, v, k, sub]) => (
              <div key={k} data-testid={`kastle-total-${k}`} className="rounded-xl bg-wf-soft p-3 text-wf-softink">
                <p className="text-xs font-semibold">{l}</p><p className="text-lg font-extrabold">{v}</p>{sub && <p className="text-xs font-semibold">{sub}</p>}
              </div>
            ))}
          </div>
          {all.map((w) => (
            <Link key={w.id} to={`/work-orders/${w.id}`} data-testid={`kastle-job-${w.id}`} className="mb-2 flex items-center gap-3 rounded-xl border border-wf-line p-3 hover:bg-wf-s2">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-bold">{w.customer?.name || "No customer"}</span>
                <span className="block truncate text-sm text-wf-mute">{w.job_description || "Untitled job"}</span>
                <span className="block text-sm text-wf-mute">{num(w.totals.total_hours)} hrs{seeMoney ? ` · ${money(w.totals.total_cost)} spent${w.totals.working_budget ? ` · ${money(w.totals.budget_remaining)} left` : ""}` : ""}</span>
              </span>
              <StatusPill label={w.status} />
            </Link>
          ))}
        </>
      )}
    </div>
  );
}
